import React from 'react'
import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'

import SocialModel, { NULL as SocialModelNULL } from './model'
import inlineStyles from './inlineStyles'
import {
  SOCIAL_SERVICE_NAMES,
  SOCIAL_ICONS,
  SOCIAL_ICONS_CDN,
  SOCIAL_SERVICE_SHARE_LINKS,
} from './constants'
import { BLOCK_TYPES } from '@client/components/Block/constants'
import AppModel, { NULL as AppModelNULL } from '@client/components/App/model'
import EditorModel, {
  NULL as EditorModelNULL,
} from '@client/components/Editor/model'
import {
  ITEM_TYPE_FORM,
  ITEM_TYPE_PAGE,
  ITEM_TYPE_LANDING_PAGE,
  ITEM_TYPE_LANDING_PAGE_SUCCESS,
  ITEM_TYPE_SURVEY,
  ITEM_TYPE_SURVEY_THANK_YOU,
  ITEM_TYPE_MAIL,
  ITEM_TYPE_RESPONDER,
} from '@client/components/Editor/settings'

const getLink = (item, type, kind) => {
  return type === BLOCK_TYPES.FOLLOW
    ? item.link || '#'
    : kind === ITEM_TYPE_MAIL || ITEM_TYPE_RESPONDER
      ? `${SOCIAL_SERVICE_SHARE_LINKS[item.name]}`
        .replace('##title##', '##subject##')
      : SOCIAL_SERVICE_SHARE_LINKS[item.name]
}

const Item = observer(
  ({ item, type, formatMessage, settings, editor = EditorModelNULL }) => {
    const link = getLink(item, type)

    let iconUrl = ''

    if (settings.size === 'small') {
      iconUrl = '-small.png'
    } else if (settings.size === 'medium') {
      iconUrl = '-medium.png'
    } else if (settings.size === 'large') {
      iconUrl = '-large.png'
    }

    const getIconUrl = (name) => {
      if (settings.design === 'color') {
        return SOCIAL_ICONS_CDN + 'color/' + SOCIAL_ICONS[name] + iconUrl
      } else if (settings.design === 'white') {
        return SOCIAL_ICONS_CDN + 'white/' + SOCIAL_ICONS[name] + iconUrl
      } else if (settings.design === 'black') {
        return SOCIAL_ICONS_CDN + 'black/' + SOCIAL_ICONS[name] + iconUrl
      }
    }

    const globalSettings = editor.settings.values
    const fontSize = settings.fontSize || globalSettings.normalText_fontSize

    const styles = {
      ...inlineStyles.img,
      fontSize: fontSize + 'px',
    }

    let width = 30
    let height = 30
    if (settings.size === 'small') {
      width = 30
      height = 30
    } else if (settings.size === 'medium') {
      width = 40
      height = 40
    } else if (settings.size === 'large') {
      width = 50
      height = 50
    }
    styles.width = `${width}px`

    if (
      [
        ITEM_TYPE_PAGE,
        ITEM_TYPE_FORM,
        ITEM_TYPE_LANDING_PAGE,
        ITEM_TYPE_LANDING_PAGE_SUCCESS,
        ITEM_TYPE_SURVEY,
        ITEM_TYPE_SURVEY_THANK_YOU,
      ].includes(editor.kind)
    ) {
      return (
        <div style={inlineStyles.td}>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.preventDefault()}
          >
            <span style={{ color: '#2a5db0' }}>
              <img
                src={getIconUrl(item.name)}
                border="0"
                alt={formatMessage(SOCIAL_SERVICE_NAMES[item.name])}
                style={styles}
                height={height}
                width={width}
              />
            </span>
          </a>
        </div>
      )
    }

    return (
      <td style={inlineStyles.td}>
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.preventDefault()}
        >
          <span style={{ color: '#2a5db0' }}>
            <img
              src={getIconUrl(item.name)}
              border="0"
              alt={formatMessage(SOCIAL_SERVICE_NAMES[item.name])}
              style={styles}
              height={height}
              width={width}
            />
          </span>
        </a>
      </td>
    )
  }
)

const flexAlign = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
}

const Social = ({
  model = SocialModelNULL,
  editor = EditorModelNULL,
  app = AppModelNULL,
}) => {
  const { formatMessage } = app
  if (
    [
      ITEM_TYPE_PAGE,
      ITEM_TYPE_FORM,
      ITEM_TYPE_LANDING_PAGE,
      ITEM_TYPE_LANDING_PAGE_SUCCESS,
      ITEM_TYPE_SURVEY_THANK_YOU,
    ].includes(editor.kind)
  ) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: flexAlign[model.settings.values.align] || 'center',
        }}
      >
        <If condition={!model.filteredServices.length}>
          <div>
            <span style={inlineStyles.placeholder}>
              {app.formatMessage('blocks.social.title')}
            </span>
          </div>
        </If>
        <For each="item" of={model.filteredServices}>
          <Item
            key={item.name}
            item={item}
            type={model.type}
            formatMessage={formatMessage}
            settings={model.settings.values}
            editor={editor}
          />
        </For>
      </div>
    )
  }
  return (
    <table
      align={model.settings.values.align}
      border="0"
      cellSpacing="0"
      cellPadding="0"
      role="presentation"
    >
      <tbody>
        <tr>
          <If condition={!model.filteredServices.length}>
            <td>
              <span style={inlineStyles.placeholder}>
                {app.formatMessage('blocks.social.title')}
              </span>
            </td>
          </If>
          <For each="item" of={model.filteredServices}>
            <Item
              key={item.name}
              item={item}
              type={model.type}
              formatMessage={formatMessage}
              settings={model.settings.values}
            />
          </For>
        </tr>
      </tbody>
    </table>
  )
}

Social.propTypes = {
  model: PropTypes.instanceOf(SocialModel),
  editor: PropTypes.instanceOf(EditorModel),
  app: PropTypes.instanceOf(AppModel),
}

export default inject('app', 'editor')(observer(Social))
